import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import { NotificationsProvider } from '@toolpad/core/useNotifications';
import { DialogsProvider } from '@toolpad/core/useDialogs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import StoreProvider from './StoreProvider';
import theme, { muiCache } from 'styles/theme';
// import { notoFont } from 'helpers/initGoogleFonts';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import RegisterPage from './pages/register';
import RegisterVerifyPage from './pages/register/verify';
import ForgotPasswordPage from './pages/forgot-password';
import ForgotPasswordVerifyPage from './pages/forgot-password/verify';
import ForgotPasswordResetPage from './pages/forgot-password/reset';
import EntryPage from './pages/entry';
import EntrySuccessPage from './pages/entry/success';
import EntryFailPage from './pages/entry/fail';

// Segments (authenticated pages)
import SegmentsLayout from './pages/segments';
import DashboardPage from './pages/segments/dashboard';
import GivePage from './pages/segments/give';
import GiveSuccessPage from './pages/segments/give/success';
import GiveFailPage from './pages/segments/give/fail';
import HistoryPage from './pages/segments/history';
import ProfilePage from './pages/segments/profile';
import ReceiptsPage from './pages/segments/receipts';
import SubscriptionPage from './pages/segments/subscription';

// Component to sync language from URL parameter
function LanguageSync() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const locale = searchParams.get('locale') || 'en';
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [searchParams, i18n]);

  return null;
}

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement && rootElement.scrollIntoView) {
      rootElement.scrollIntoView({ block: 'start' });
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <div>
      <CacheProvider value={muiCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationsProvider>
            <DialogsProvider>
              <StoreProvider>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='en-gb'>
                  <BrowserRouter basename="/giving">
                    <LanguageSync />
                    <ScrollToTop />
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/login" element={<HomePage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/register/verify" element={<RegisterVerifyPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/forgot-password/verify" element={<ForgotPasswordVerifyPage />} />
                      <Route path="/forgot-password/reset" element={<ForgotPasswordResetPage />} />
                      <Route path="/do-not-require-a-receipt" element={<EntryPage />} />
                      <Route path="/do-not-require-a-receipt/success" element={<EntrySuccessPage />} />
                      <Route path="/do-not-require-a-receipt/fail" element={<EntryFailPage />} />

                      {/* Protected routes */}
                      <Route path="/" element={<SegmentsLayout />}>
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="give" element={<GivePage />} />
                        <Route path="give/success" element={<GiveSuccessPage />} />
                        <Route path="give/fail" element={<GiveFailPage />} />
                        <Route path="history" element={<HistoryPage />} />
                        <Route path="subscriptions" element={<SubscriptionPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="receipts" element={<ReceiptsPage />} />
                      </Route>

                      {/* 404 */}
                      <Route path="/not-found" element={<NotFoundPage />} />
                      <Route path="*" element={<Navigate to="/not-found" replace />} />
                    </Routes>
                  </BrowserRouter>
                  <ToastContainer />
                </LocalizationProvider>
              </StoreProvider>
            </DialogsProvider>
          </NotificationsProvider>
        </ThemeProvider>
      </CacheProvider>
    </div>
  );
}

export default App;
